import { ButtonPrimary, ButtonSecondary } from "components/Atoms/Button";
import React from "react";
import { routes } from "utilities/routing/generatedRoutes";
import { DetailSidePanel } from "../DetailSidePanel";
import { FlightSidePanelDetails } from "./FlightSidePanelDetails";
import { useOpenSidePanel } from "../SidePanel";
import { useOpenNewFlightSidePanel } from "./NewFlightSidePanel";
import { RightClickMenu } from "components/RightClickMenu/RightClickMenuContext";
import { rightMenuForSidePanel } from "components/RightClickMenu/rightClickMenuUtils";
import { invalidateFlightsCache } from "utilities/api/flightQuery";
import { useQueryClient } from "@tanstack/react-query";

export interface FlightSidePanelProps {
  flightId: number
  trigger: JSX.Element
}

const AddSegmentButton = ({ flightId }: { flightId: number }) => {
  const openNewFlightSidePanel = useOpenNewFlightSidePanel();

  return <ButtonSecondary label={RESX.Flight.AddSegment} onClick={() => openNewFlightSidePanel(flightId)} />
}

export const FlightSidePanel = (props: FlightSidePanelProps) => {
  const queryClient = useQueryClient();

  return <DetailSidePanel
    detailUrl={routes.flight_Detail(props.flightId).absolute}
    apiCall={() => window.CoreApi.Flight.GetSidePanelData(props.flightId)}
    apiKey="flight"
    entityId={props.flightId}
    title={RESX.Flight.resxFlight}
    titleFunc={(data) => data.flight.flightType === "private" ? RESX.Flight.PrivateFlight : RESX.Flight.resxFlight}
    footerContent={data => <>
      {(data.flight.flightType === "commercial" && site.permissions.flight.canCreate) && <AddSegmentButton flightId={data.flight.flightId} />}
      <ButtonPrimary href={routes.flight_Detail(props.flightId).absolute} label={RESX.Buttons.DetailView} />
    </>}
    trigger={<RightClickMenu
      menuItems={rightMenuForSidePanel(routes.flight_Detail(props.flightId).absolute, {
        duplicate: {
          canDuplicate: site.permissions.flight.canCreate,
          duplicateFn: async () => {
            const resp = await window.CoreApi.Flight.CopyFlight({ flightId: props.flightId });
            return resp.data;
          },
          sidePanelToOpen: "flight",
          invalidateCache: () => invalidateFlightsCache(queryClient),
        }
      })}
    >
      {props.trigger}
    </RightClickMenu>}
  >
    {(data, refetch) => <FlightSidePanelDetails
      flightSidePanelData={data}
      refetch={refetch}
    />}
  </DetailSidePanel>
};

export const useOpenFlightSidePanel = () => {
  const openSidePanel = useOpenSidePanel();

  const openFlightSidePanel = React.useCallback((flightId: number) => {
    openSidePanel(<DetailSidePanel
      apiCall={() => window.CoreApi.Flight.GetSidePanelData(flightId)}
      apiKey="flight"
      entityId={flightId}
      title={RESX.Flight.resxFlight}
      footerContent={data => <>
        {(data.flight.flightType === "commercial" && site.permissions.flight.canCreate) && <AddSegmentButton flightId={data.flight.flightId} />}
        <ButtonPrimary href={routes.flight_Detail(flightId).absolute} label={RESX.Buttons.DetailView} />
      </>}
      skipSidePanelBase
    >
      {(data, refetch) => <FlightSidePanelDetails
        flightSidePanelData={data}
        refetch={refetch}
      />}
    </DetailSidePanel>, `flight-side-${flightId}`);

  }, [openSidePanel]);

  return openFlightSidePanel;
}
